import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme } from '../context/ThemeContext';
import { usePricing } from '../hooks/usePricing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PublicNavbar from '../components/PublicNavbar';
import SEOFooter from '../components/SEOFooter';
import ProgrammesRail from '../components/ProgrammesRail';
import BackToTop from '../components/BackToTop';
import { useScrollLock } from '../hooks/useScrollLock';
import { PageMeta, SITE_URL } from '../components/PageMeta';
import { TrialModal } from '../components/TrialModal';
import { trackTrialCTAClick, trackTrialModalOpen, trackAssessmentCTA } from '../utils/analytics';
import { useLibraryStats } from '../hooks/useLibraryStats';
import { TrustStackStrip } from '../components/TrustStack';
import {
  Sparkles, Users, Award, ShieldCheck, Building2,
  ArrowRight, CheckCircle2, ClipboardList, Globe, GraduationCap,
  FileText, BarChart3, Palette, Target, FolderOpen,
  RefreshCw, Zap, Check, X, Maximize2, Star,
  Clock, Lock, ShieldAlert, Download,
  AlertTriangle, TrendingUp, ChevronDown, CheckCircle, Play, BadgeCheck,
} from 'lucide-react';

const DEFAULT_LOGO = '/cpdforge-icon.webp';

/* ─── Poster map for click-to-play video previews (R2/CDN) ─── */
const LANDING_POSTER_MAP = {
  'https://assets.healthandsafetycollege.com/videos/ai-review-demo.mp4': 'https://assets.healthandsafetycollege.com/images/video-previews/poster-ai-review.webp',
  'https://assets.healthandsafetycollege.com/videos/course-generator-demo.mp4': 'https://assets.healthandsafetycollege.com/images/video-previews/poster-course-gen.webp',
  'https://assets.healthandsafetycollege.com/videos/image-creation-demo.mp4': 'https://assets.healthandsafetycollege.com/images/video-previews/poster-enrichment.webp',
};

/* ─── Click-to-play demo video (zero bytes on initial load) ─── */
function LandingDemoVideo({ src, caption, onExpand }) {
  const [playing, setPlaying] = useState(false);
  const poster = LANDING_POSTER_MAP[src];
  return (
    <>
      <div className={`relative group rounded-xl overflow-hidden border border-slate-200/60 dark:border-slate-700/60 shadow-lg bg-slate-900 cursor-pointer w-full transition-transform duration-300 ${!playing ? 'hover:scale-[1.02] hover:shadow-xl hover:shadow-indigo-500/10' : ''}`}
        onClick={() => { if (!playing) setPlaying(true); else onExpand(); }}
        data-testid="landing-demo-video">
        {playing ? (
          <video autoPlay loop muted playsInline preload="none" className="w-full aspect-video object-cover" onClick={e => { e.stopPropagation(); onExpand(); }}>
            <source src={src} type="video/mp4" />
          </video>
        ) : (
          <div className="w-full aspect-video relative">
            {poster ? (
              <img src={poster} alt="" loading="lazy" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent demo-shimmer pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-white/20 demo-pulse-ring" />
                <div className="relative bg-white/20 backdrop-blur-sm rounded-full p-4 shadow-lg group-hover:bg-white/30 transition-colors">
                  <Play className="w-6 h-6 text-white fill-white" />
                </div>
              </div>
            </div>
          </div>
        )}
        {playing && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg">
              <Maximize2 className="w-4 h-4 text-slate-900 dark:text-white" />
            </div>
          </div>
        )}
      </div>
      {caption && <p className="mt-2.5 text-[11px] text-slate-400 dark:text-slate-500 text-center leading-snug max-w-[340px]">{caption}</p>}
    </>
  );
}

/* ─── Pillar strip data ─── */
const PILLARS = [
  { id: 'create',  icon: Sparkles,    label: 'Create structured courses',     color: '#6366f1' },
  { id: 'improve', icon: RefreshCw,   label: 'Fix existing training',         color: '#8b5cf6' },
  { id: 'enrich',  icon: Zap,         label: 'Enhance automatically',         color: '#f59e0b' },
  { id: 'manage',  icon: Users,       label: 'Manage training & learners',    color: '#2563eb' },
  { id: 'detect',  icon: ShieldCheck, label: 'Detect risks & stay compliant', color: '#059669', primary: true },
];

/* ─── Problem framing ─── */
const PROBLEMS = [
  { icon: Clock,     title: 'Slow to create',        desc: 'Building compliant training manually takes days or weeks. Regulatory updates take even longer to reflect.' },
  { icon: Lock,      title: 'Content goes stale',    desc: 'Pre-built course libraries drift out of alignment with live regulations. Nobody notices until audit.' },
  { icon: BarChart3, title: 'No intelligence loop',  desc: 'No visibility into which regulations changed, which courses are affected, or what training gaps exist.' },
  { icon: Users,     title: 'Manual admin burden',   desc: 'Assigning courses, tracking completion, chasing overdue learners, generating reports — all manual.' },
];

/* ─── Core capability blocks ─── */
const CAPABILITIES = [
  {
    id: 'create', icon: Sparkles, color: '#6366f1', title: 'Create structured courses',
    desc: 'Describe your topic, audience, and industry. AI generates a complete, structured course with lessons, learning objectives, scenarios, and knowledge checks.',
    points: ['Full structured course from a single prompt', 'Industry-aware content from organisational templates', 'Course outline preview before generation', 'AI-generated images and covers for every lesson', 'Content quality validation before saving'],
  },
  {
    id: 'improve', icon: RefreshCw, color: '#8b5cf6', title: 'Fix existing training',
    desc: 'AI Review analyses your courses for training gaps, weak content, and missing compliance topics — then proposes non-destructive improvements you approve before they\'re applied.',
    points: ['Non-destructive AI Review — enhances, never replaces', 'Section-level rewrite, expand, and new scenario modes', 'Adds new lessons to fill compliance and knowledge gaps', 'Preservation guard protects existing content depth', 'Regenerate lessons, quizzes, and images individually'],
  },
  {
    id: 'enrich', icon: Zap, color: '#f59e0b', title: 'Enhance content automatically',
    desc: 'When AI Review adds new lessons, knowledge checks and lesson images are generated automatically in the background — processing continues even if you navigate away.',
    points: ['Background quiz generation for every lesson', 'Background image generation with CDN delivery', 'Processing survives refresh and navigation', 'Automatic expansion of thin or incomplete content'],
  },
  {
    id: 'manage', icon: Users, color: '#2563eb', title: 'Manage training & learners',
    desc: 'A complete training management system. Manage learner profiles, assign courses by team or department, track completion, issue certificates, and maintain auditable records.',
    points: ['Dedicated learner portal with course player', 'Assignments with team and department cascading', 'Branded, verifiable certificates with expiry alerts', 'Completion tracking and training records', 'CSV learner import with role mapping', 'Audit evidence export (PDF / CSV)'],
  },
];

/* ─── Compliance Intelligence sub-capabilities ─── */
const INTELLIGENCE = [
  { icon: Globe,         title: 'Regulatory Intelligence',  desc: 'Monitor regulatory changes across HR, OHS, sustainability, quality, and corporate governance.' },
  { icon: Target,        title: 'Course Impact Detection',  desc: 'When regulations change, AI identifies which courses are affected and generates specific update plans.' },
  { icon: ShieldAlert,   title: 'Risk Radar',               desc: 'Identify compliance gaps and at-risk training areas before they become incidents.' },
  { icon: ClipboardList, title: 'Compliance Advisor',       desc: 'AI analyses your training programme and recommends targeted improvements.' },
  { icon: Sparkles,      title: 'Fix Engine AI',            desc: 'Automatically generate remediation plans for identified compliance gaps and outdated training.' },
  { icon: FileText,      title: 'Compliance Playbooks',     desc: 'Operational playbooks that standardise compliance processes across your organisation.' },
];

/* ─── Signup tiers ─── */
const SIGNUP_TIERS = [
  { id: 'starter', label: 'Starter', desc: 'AI course generation for individuals', workspace: 'organisation', plan: 'starter' },
  { id: 'professional', label: 'Pro', desc: 'Full platform for training teams', workspace: 'organisation', plan: 'professional', popular: true },
  { id: 'partner', label: 'Partner', desc: 'Multi-client management', workspace: 'partner', plan: 'partner' },
];

/* ─── Export formats ─── */
const EXPORTS = [
  { label: 'SCORM',      desc: 'Upload to any LMS',          color: '#6366f1' },
  { label: 'PowerPoint', desc: 'Download and present',        color: '#f59e0b' },
  { label: 'PDF',        desc: 'Print or share instantly',    color: '#ef4444' },
  { label: 'HTML',       desc: 'Embed or host anywhere',      color: '#059669' },
];

/* ─── Audiences ─── */
const AUDIENCES = [
  { icon: ShieldCheck,   title: 'Health & Safety Consultants', desc: 'Create structured training courses for clients across industries.' },
  { icon: ClipboardList, title: 'Compliance Managers',         desc: 'Build and track mandatory compliance training with certification.' },
  { icon: Users,         title: 'Training & HR Teams',        desc: 'Deliver internal training programmes and monitor completion.' },
  { icon: Building2,     title: 'Partner Training Providers',  desc: 'Manage training delivery for multiple client organisations.' },
];

/* ─── Pricing ─── */
const PLANS_PREVIEW = [
  { name: 'Starter',      price: '29',  desc: 'For individuals starting with AI course generation.',  color: '#64748b', rgb: '100,116,139', bestFor: 'Individuals',  keyMetric: '10 AI courses / month',  gradient: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', gradientDark: 'linear-gradient(180deg, #0f172a 0%, #0f172a 100%)' },
  { name: 'Professional', price: '79',  desc: 'For trainers building structured learning programmes.', color: '#8b5cf6', rgb: '139,92,246', bestFor: 'Trainers',     keyMetric: '50 AI courses / month',  gradient: 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)', gradientDark: 'linear-gradient(180deg, #1e1033 0%, #0f172a 100%)', popular: true },
];

export default function Landing() {
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedTier, setSelectedTier] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const { trialDays } = usePricing();
  const { stats: libStats } = useLibraryStats();
  useScrollLock(!!lightboxVideo);
  const [rateLimited, setRateLimited] = useState(false);
  // P1 / Item 3: MFA challenge state
  const [mfaPendingToken, setMfaPendingToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [isRecoveryCode, setIsRecoveryCode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [exitEmailSubmitted, setExitEmailSubmitted] = useState(false);
  const { login, verifyMfa, register, forgotPassword, user } = useAuth();
  const { branding } = useBranding();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authRef = useRef(null);
  const heroRef = useRef(null);

  const PLAN_INFO = { starter: { name: 'Starter', value: 29 }, professional: { name: 'Professional', value: 79 }, partner: { name: 'Partner', value: 149 } };
  const signupPlanId = searchParams.get('plan') || searchParams.get('signup');
  const intentParam = searchParams.get('intent');
  const authParam = searchParams.get('auth');
  const returnToParam = searchParams.get('returnTo');
  const planContext = signupPlanId && PLAN_INFO[signupPlanId]
    ? { plan_id: signupPlanId, plan_name: PLAN_INFO[signupPlanId].name, plan_value: PLAN_INFO[signupPlanId].value }
    : {};

  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialPlanId, setTrialPlanId] = useState(null);
  const modalDismissedRef = useRef(false);

  const brandName = branding?.company_name || 'CPDForge AI';
  const logoSrc = branding?.logo_base64 || DEFAULT_LOGO;

  useEffect(() => {
    if (!user) return;
    // Honour an explicit post-auth redirect first (e.g. programme-purchase checkout).
    // This must run BEFORE the dashboard/partner fallback, otherwise this effect would
    // race and override handleSubmit's checkout redirect, hijacking buyers to onboarding.
    const par = localStorage.getItem('post_auth_redirect');
    if (par) { localStorage.removeItem('post_auth_redirect'); navigate(par); return; }
    if (returnToParam) { navigate(returnToParam); return; }
    navigate(user.workspace_type === 'partner' ? '/partner' : '/dashboard');
  }, [user, navigate, returnToParam]);
  // Robust scroll-to-sign-in. The auth card sits far down the page; lazy images
  // above it reflow AFTER a scroll target is computed, so a single immediate
  // scrollIntoView lands at a stale offset (overshoots — card ends up above the
  // viewport). Fix: defer until the card has re-rendered, then scroll, then run
  // ONE corrective pass once the layout settles so it lands centred.
  const scrollAuthIntoView = useCallback(() => {
    const el = () => authRef.current;
    const HEADER_H = 72;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const node = el();
        if (!node) return;
        const r = node.getBoundingClientRect();
        if (r.top < HEADER_H || r.bottom > window.innerHeight) {
          node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 550);
    }));
  }, []);



  useEffect(() => {
    if (user) return;
    // Handle ?auth=login&returnTo= (from MFA guide auth-aware links)
    if (authParam === 'login') {
      setAuthMode('login');
      scrollAuthIntoView();
      return;
    }
    // Phase 2: Handle both trial and buy intents
    if (intentParam === 'trial' || intentParam === 'buy' || intentParam === 'programme' || signupPlanId) {
      setAuthMode('register');
      scrollAuthIntoView();
      setTimeout(() => document.querySelector('[data-testid="auth-email-input"]')?.focus(), 700);
    } else if (intentParam === 'login') {
      setAuthMode('login');
      scrollAuthIntoView();
    }
  }, [intentParam, signupPlanId, user, authParam]);

  useEffect(() => {
    if (signupPlanId && PLAN_INFO[signupPlanId]) setSelectedTier(signupPlanId);
  }, [signupPlanId]);

  const handleTrialCTA = useCallback((sourcePlanId) => {
    trackTrialModalOpen('landing', sourcePlanId || undefined);
    setTrialPlanId(sourcePlanId || null);
    setTrialModalOpen(true);
    modalDismissedRef.current = false;
    scrollAuthIntoView();
  }, []);

  const handleTrialModalCreate = useCallback(() => {
    trackTrialCTAClick(trialPlanId || undefined);
    setTrialModalOpen(false);
    setAuthMode('register');
    setTimeout(() => document.querySelector('[data-testid="auth-email-input"]')?.focus(), 200);
  }, [trialPlanId]);

  const handleTrialModalSignIn = useCallback(() => {
    setTrialModalOpen(false);
    setAuthMode('login');
    scrollAuthIntoView();
  }, []);

  const handleTrialModalClose = useCallback(() => {
    setTrialModalOpen(false);
    modalDismissedRef.current = true;
  }, []);

  const handleHeroMouse = useCallback((e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    setMousePos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setShowStickyBar(window.scrollY > 600);
      // Exit intent: DISABLED FOR NOW (keep code for future)
      // Uncomment below to re-enable exit intent modal at 80% scroll
      /*
      if (!user && !exitEmailSubmitted && !showExitIntent) {
        const scrollPercentage = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercentage > 80) {
          setShowExitIntent(true);
        }
      }
      */
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [user, exitEmailSubmitted, showExitIntent]);

  const scrollToAuth = (mode = 'register') => {
    setAuthMode(mode);
    scrollAuthIntoView();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRateLimited(false);
    try {
      if (authMode === 'reset') {
        await forgotPassword(email);
        toast.success('If an account exists with that email, a reset link has been sent.');
        setAuthMode('login'); setPassword(''); setConfirmPassword('');
      } else if (authMode === 'login') {
        const u = await login(email, password);
        // P1 / Item 3: server returned an MFA challenge
        if (u && u.mfaRequired) {
          setMfaPendingToken(u.mfaPendingToken);
          setAuthMode('mfa');
          setLoading(false);
          return;
        }
        // MFA enforcement: privileged user without MFA. Use a hard navigation
        // so the setup page mounts cleanly with the fresh setup token (a
        // client-side navigate can race the auth-state change and not land).
        if (u && u.mfaSetupRequired) {
          localStorage.setItem('mfa_setup_mode', '1');
          toast.info('Multi-Factor Authentication setup is required for your account.', { duration: 6000 });
          window.location.assign('/mfa-setup');
          return;
        }
        toast.success('Welcome back!');
        const _par = localStorage.getItem('post_auth_redirect');
        if (_par) { localStorage.removeItem('post_auth_redirect'); navigate(_par); return; }
        navigate(u.workspace_type === 'partner' ? '/partner' : '/dashboard');
      } else if (authMode === 'mfa') {
        const u = await verifyMfa(mfaPendingToken, mfaCode, isRecoveryCode, trustDevice);
        toast.success('Welcome back!');
        setMfaPendingToken(null); setMfaCode(''); setIsRecoveryCode(false); setTrustDevice(false);
        const _parMfa = localStorage.getItem('post_auth_redirect');
        if (_parMfa) { localStorage.removeItem('post_auth_redirect'); navigate(_parMfa); return; }
        navigate(u.workspace_type === 'partner' ? '/partner' : '/dashboard');
      } else {
        if (password.length < 8) { toast.error('Password must be at least 8 characters'); setLoading(false); return; }
        const tier = SIGNUP_TIERS.find(t => t.id === selectedTier) || SIGNUP_TIERS[1];
        
        // Phase 2 (Revised): Check if user intent is "buy" (not trial)
        const isBuyIntent = intentParam === 'buy';
        // Programme purchase: a credential purchase journey — NEVER a platform trial.
        const isProgrammePurchase = intentParam === 'programme';
        
        // Build registration payload with skip_trial flag for buy-intent
        const registrationPayload = {
          email,
          password,
          workspace_type: tier.workspace,
        };
        
        if (isBuyIntent || isProgrammePurchase) {
          // Buy / programme-purchase: never create a platform trial.
          registrationPayload.skip_trial = true;
        } else {
          // Trial-intent: Include trial plan
          if (tier.plan) {
            registrationPayload.trial_plan = tier.plan;
          }
        }
        
        const u = await register(registrationPayload);
        toast.success('Account created!');

        // Programme-purchase journey: defer redirect to the post_auth_redirect effect,
        // which consumes localStorage['post_auth_redirect'] once `user` is set and routes
        // straight back to the programme checkout. We MUST NOT navigate here (or fall through
        // to the dashboard navigate below) or we'd hijack the buyer into platform onboarding.
        if (isProgrammePurchase) {
          return;
        }
        
        // Phase 2: If buy intent, redirect to checkout immediately
        if (isBuyIntent && signupPlanId) {
          toast.info('Redirecting to secure checkout...');
          try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const res = await axios.post(
              `${API_URL}/api/checkout/create-subscription`,
              { 
                plan: signupPlanId, 
                interval: 'monthly', 
                skip_trial: true,
                origin_url: window.location.origin 
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.url) {
              window.location.href = res.data.url; // Redirect to Stripe
              return; // Don't navigate to dashboard
            }
          } catch (checkoutErr) {
            console.error('Phase 2: Checkout failed:', checkoutErr);
            toast.error('Checkout failed. Redirecting to pricing page.');
            navigate('/pricing?error=checkout_failed');
            setLoading(false);
            return;
          }
        }
        
        navigate(u.workspace_type === 'partner' ? '/partner' : '/dashboard');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong';
      if (msg.toLowerCase().includes('too many')) {
        setRateLimited(true);
      } else if (msg.toLowerCase().includes('learner') && msg.toLowerCase().includes('/learner/login')) {
        toast.error('This email is a learner account.', { duration: 5000, action: { label: 'Go to Learner Login', onClick: () => navigate('/learner/login') } });
      } else {
        toast.error(msg);
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 overflow-x-clip">
      <PageMeta
        title="AI Compliance Training Platform — Create, Improve & Manage"
        description="The AI compliance training platform. Generate structured courses, improve content automatically, monitor regulatory changes, and generate audit & accreditation-ready evidence."
        path="/"
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "CPDForge AI",
          "legalName": "CPDFORGE AI LTD",
          "url": SITE_URL,
          "logo": `${SITE_URL}/cpdforge-icon.webp`,
          "description": "AI-powered compliance training platform for creating, improving, and managing CPD courses at scale.",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "66 Paul Street",
            "addressLocality": "London",
            "addressCountry": "GB",
            "postalCode": "EC2A 4NA"
          },
          "sameAs": []
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "CPDForge AI",
          "url": SITE_URL,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${SITE_URL}/library?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "CPDForge AI",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "The AI compliance training platform. Generate structured courses, improve content automatically, monitor regulatory changes, track learners, and deploy training at scale.",
          "url": SITE_URL,
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "29",
            "highPrice": "699",
            "priceCurrency": "GBP",
            "offerCount": "5",
            "availability": "https://schema.org/OnlineOnly"
          }
        })}</script>
      </PageMeta>
      <PublicNavbar onSignIn={() => scrollToAuth('login')} />

      {/* Sticky CTA bar — appears after scrolling past hero */}
      {showStickyBar && !user && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-white/90 dark:bg-slate-950/90 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800/60 shadow-sm" data-testid="sticky-cta-bar">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logoSrc} alt={brandName} className="w-7 h-7 rounded-lg" />
              <span className="text-sm font-bold text-slate-900 dark:text-white hidden sm:inline">{brandName}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 hidden md:inline">&bull; AI Compliance Training Platform</span>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { handleTrialCTA(null); }} data-testid="sticky-trial-cta"
                className="h-9 px-5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-0 shadow-md shadow-indigo-500/20">
                Start Free Trial <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ HERO ═══════════ */}
      <section ref={heroRef} onMouseMove={handleHeroMouse} className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-violet-50/60 to-purple-50/30 dark:from-slate-950 dark:via-indigo-950/20 dark:to-slate-950" />
        <div className="absolute inset-0 hero-dots" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[1100px] h-[650px] rounded-full opacity-[0.14] dark:opacity-[0.10] blur-3xl" style={{ background: 'radial-gradient(ellipse, #818cf8 0%, #c084fc 40%, transparent 70%)' }} />
        <div className="hidden md:block absolute top-48 -left-48 w-96 h-96 rounded-full opacity-[0.08] dark:opacity-[0.05] blur-3xl bg-violet-400" />
        <div className="hidden md:block absolute top-24 -right-48 w-[420px] h-[420px] rounded-full opacity-[0.06] dark:opacity-[0.04] blur-3xl bg-indigo-500" />
        <div className="absolute inset-0 pointer-events-none transition-[background] duration-300 ease-out" style={{ background: `radial-gradient(700px circle at ${mousePos.x}% ${mousePos.y}%, rgba(129,140,248,0.08), transparent 55%)` }} />

        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-indigo-200/80 dark:border-indigo-800/60 mb-8 bg-white/70 dark:bg-slate-900/70 backdrop-blur-sm text-indigo-600 dark:text-indigo-400 shadow-sm" data-testid="hero-badge">
            <ShieldCheck className="w-4 h-4" />
            Compliance, Governance &amp; Credential Platform
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-[1.2] mb-6" data-testid="hero-heading">
            Accredited Compliance &amp; AI-Governance Credentials{' '}
            <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 dark:from-indigo-400 dark:via-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
              Governed, Accredited &amp; Verifiable
            </span>
          </h1>

          <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto mb-8" data-testid="hero-subheading">
            CPDForge turns compliance and AI-governance training into Core Compliant accredited, publicly verifiable credentials — created by subject matter experts, grounded by International Standards, augmented with AI, governed by the CPFPSI quality framework, and kept current by regulatory intelligence.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Button onClick={() => handleTrialCTA(null)} data-testid="hero-start-trial" data-cta="primary-trial"
              className="btn-shine h-12 px-8 rounded-full text-base font-semibold text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35 transition-all active:scale-[0.97] bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-0">
              Start 14-Day Free Trial <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button onClick={() => {
              trackAssessmentCTA({
                text: 'Find Your Platform',
                type: 'secondary',
                destination: '/compliance-training-assessment',
                position: 'hero',
                pageType: 'homepage',
              });
              sessionStorage.setItem('assessment_entry_source', '/');
              sessionStorage.setItem('assessment_cta_location', 'homepage_hero');
              navigate('/compliance-training-assessment');
            }} variant="outline" data-testid="hero-assessment-cta"
              className="h-12 px-8 rounded-full text-base font-semibold border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 hover:border-indigo-400 dark:hover:border-indigo-600 hover:shadow-md transition-all">
              Find Your Platform <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-500" data-testid="hero-trust-line">
            No credit card required &bull; Full platform access for {trialDays} days &bull; Audit & accreditation-ready training
          </p>

          {/* Trust signal bar */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400 dark:text-slate-500 font-medium" data-testid="hero-trust-bar">
            <span className="flex items-center gap-1.5"><img src="/brand/core-compliant-icon.png" alt="" className="w-3.5 h-3.5 object-contain" /> Core Compliant Accredited</span>
            <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-emerald-500" /> Publicly Verifiable Credentials</span>
            <span className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5 text-violet-400" /> Credential Pathways</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-indigo-400" /> CPFPSI Governed &amp; QA-Verified</span>
          </div>

          {/* Trust stack — CPDForge → CPFPSI → Core Compliant */}
          <div className="mt-6 max-w-2xl mx-auto">
            <TrustStackStrip dataTestId="home-trust-stack-strip" />
          </div>

          {/* Core System Explanation */}
          <div className="mt-10 max-w-3xl mx-auto">
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed text-center px-6 py-4 rounded-xl bg-slate-50/60 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/60">
              <strong className="text-slate-900 dark:text-white">Most platforms stop at course creation.</strong> CPDForge closes the compliance loop: regulatory changes trigger AI-powered course improvements, which update your training automatically — creating a continuous cycle of compliance performance.
            </p>
          </div>

          {/* Product Screenshot */}
          <div className="mt-12 max-w-4xl mx-auto" data-testid="hero-product-image">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-indigo-500/10 dark:shadow-indigo-500/5 border border-slate-200/50 dark:border-slate-800/50">
              <img src="/product-hero.webp" alt="CPDForge AI Course Review Dashboard" className="w-full h-auto" width={1536} height={1024} loading="eager" fetchPriority="high" />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PILLAR STRIP ═══════════ */}
      <section className="py-6 border-y border-slate-200/60 dark:border-slate-800/60 bg-slate-50/60 dark:bg-slate-900/30" data-testid="pillar-strip">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <button key={p.id} onClick={() => document.getElementById(`cap-${p.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  data-testid={`pillar-${p.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all hover:shadow-md ${
                    p.primary
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-300/70 dark:border-emerald-700/50 hover:border-emerald-400 dark:hover:border-emerald-600'
                      : 'bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 hover:border-indigo-300 dark:hover:border-indigo-700/50'
                  }`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${p.color}12` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: p.color }} />
                  </div>
                  <span className={`text-sm font-semibold leading-tight ${p.primary ? 'text-emerald-800 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-300'}`}>{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ LIFECYCLE: GENERATE → GOVERN → ACCREDIT → VERIFY ═══════════ */}
      <section className="py-16 sm:py-20 border-y border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-950" data-testid="lifecycle-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">From generation to a credential buyers can trust</h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              CPDForge doesn&rsquo;t just generate training. Every programme moves through a governed lifecycle &mdash;
              so the credential at the end is accredited and independently verifiable.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="lifecycle-steps">
            {[
              { n: '1', t: 'Generate', d: 'AI builds bespoke, standards-grounded training.', cls: 'text-violet-500', to: null },
              { n: '2', t: 'Govern', d: 'CPFPSI 14-criterion QA + human SME sign-off.', cls: 'text-indigo-500', to: '/professional-standards-institute' },
              { n: '3', t: 'Accredit', d: 'Core Compliant accredits the programme & credential.', cls: 'text-orange-500', to: '/accreditation', cc: true },
              { n: '4', t: 'Verify', d: 'Every credential is publicly verifiable via QR.', cls: 'text-emerald-500', to: '/verification' },
            ].map((s, i) => {
              const inner = (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-2xl font-extrabold ${s.cls}`}>{s.n}</span>
                    <span className="text-xs font-bold uppercase tracking-wide text-slate-300 dark:text-slate-600">Step {s.n}</span>
                    {s.cc && <img src="/brand/core-compliant-icon.png" alt="Core Compliant accredited" title="Core Compliant accredited" className="w-5 h-5 object-contain ml-auto" />}
                  </div>
                  <p className="text-lg font-bold mb-1.5">{s.t}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.d}</p>
                </>
              );
              const base = 'p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 h-full';
              return s.to
                ? <Link key={i} to={s.to} className={`${base} block hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors`} data-testid={`lifecycle-step-${i}`}>{inner}</Link>
                : <div key={i} className={base} data-testid={`lifecycle-step-${i}`}>{inner}</div>;
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ AI GOVERNANCE & ASSURANCE ═══════════ */}
      <section className="py-16 sm:py-20 relative overflow-hidden bg-gradient-to-b from-emerald-50/60 to-white dark:from-emerald-950/20 dark:to-slate-950" data-testid="ai-governance-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-100/70 dark:bg-emerald-950/40 px-3 py-1 rounded-full mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Flagship pillar
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">
                AI Governance &amp; Assurance
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
                A complete authority map for ISO/IEC 42001, the EU AI Act and NIST AI RMF —
                standards, regulations, roles and objectives, each linked to verifiable,
                QA-grounded credentials kept current by regulatory intelligence.
              </p>
              <button
                onClick={() => navigate('/ai')}
                data-testid="landing-ai-hub-cta"
                className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-md shadow-emerald-500/20 transition-all"
              >
                Explore the AI governance hub <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Govern the AIMS', sub: 'ISO/IEC 42001', path: '/ai/govern' },
                { label: 'Comply with AI law', sub: 'EU AI Act · NIST', path: '/ai/comply' },
                { label: 'Audit & assure', sub: 'Internal & lead audit', path: '/ai/audit' },
                { label: 'ISO/IEC 42001', sub: 'Standard hub', path: '/ai/standards/iso-42001' },
              ].map((c) => (
                <button key={c.path} onClick={() => navigate(c.path)}
                  data-testid={`landing-ai-link-${c.path.replace(/\//g, '-')}`}
                  className="text-left p-4 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 hover:border-emerald-300 dark:hover:border-emerald-700/60 hover:shadow-md transition-all group">
                  <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-400">{c.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.sub}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ SPECIALIST PROGRAMMES & CREDENTIAL FAMILIES ═══════════ */}
      <section className="py-16 sm:py-20 bg-white dark:bg-slate-950 border-b border-slate-200/60 dark:border-slate-800/60" data-testid="programmes-showcase-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-400 bg-violet-100/70 dark:bg-violet-950/40 px-3 py-1 rounded-full mb-4">
              <GraduationCap className="w-3.5 h-3.5" /> Specialist Programmes &amp; Credentials
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-3">
              Credentialed pathways, not just courses
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Multi-course Specialist Programmes carry learners along a credential family — from
              Awareness through to Leadership &amp; Governance — each one Core Compliant accredited,
              CPFPSI-governed, and independently verifiable.
            </p>
          </div>

          {/* Progression ladder */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="credential-ladder">
            {[
              { t: 'Awareness', d: 'Foundational understanding for every employee.', c: 'text-sky-500' },
              { t: 'Practitioner', d: 'Applied competence for day-to-day practitioners.', c: 'text-indigo-500' },
              { t: 'Auditor', d: 'Internal & lead-audit capability against the standard.', c: 'text-violet-500' },
              { t: 'Manager', d: 'Management-system ownership and oversight.', c: 'text-amber-500' },
              { t: 'Leadership', d: 'Board, executive & governance leadership.', c: 'text-orange-500' },
            ].map((s, i) => (
              <div key={s.t} className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30" data-testid={`ladder-step-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wide ${s.c}`}>Level {i + 1}</span>
                  {i < 4 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />}
                </div>
                <p className="text-base font-bold text-slate-900 dark:text-white mb-1">{s.t}</p>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          {/* Flagship families + CTA */}
          <div className="grid lg:grid-cols-3 gap-4">
            {[
              { label: 'AI Governance & Assurance', sub: 'ISO/IEC 42001 · AI Lead Auditor · Certified AI Assurance Leader', path: '/ai', accent: 'emerald' },
              { label: 'ISO Management Systems', sub: 'ISO 9001 · 14001 · 45001 · 27001 — internal & lead auditor pathways', path: '/credentials', accent: 'indigo' },
              { label: 'Lean Six Sigma', sub: 'Yellow → Green → Black Belt progression', path: '/credentials', accent: 'violet' },
            ].map((f) => (
              <button key={f.label} onClick={() => navigate(f.path)} data-testid={`programme-family-${f.accent}`}
                className="text-left p-5 rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 hover:border-violet-300 dark:hover:border-violet-700/60 hover:shadow-md transition-all group">
                <p className="text-base font-bold text-slate-900 dark:text-white group-hover:text-violet-700 dark:group-hover:text-violet-400">{f.label}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{f.sub}</p>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <Button onClick={() => navigate('/library/programmes')} data-testid="programmes-showcase-cta"
              className="h-11 px-6 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-0">
              Explore Specialist Programmes <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button onClick={() => navigate('/credentials')} variant="outline" data-testid="credential-families-cta"
              className="h-11 px-6 rounded-full text-sm font-semibold border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30">
              Browse Credential Families
            </Button>
          </div>
        </div>
      </section>

      {/* ═══════════ SEE CPDFORGE IN ACTION ═══════════ */}
      <section className="pt-20 pb-2 relative overflow-hidden bg-gradient-to-b from-slate-50/60 to-white dark:from-slate-900/40 dark:to-slate-950" data-testid="product-story-section">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-800/60 bg-white/70 dark:bg-slate-900/70 backdrop-blur-sm mb-5" data-testid="product-story-badge">
              See CPDForge AI in Action
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-4" data-testid="product-story-heading">
              From training gaps to audit-ready compliance — in minutes
            </h2>
            <p className="text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
              CPDForge AI identifies risks, creates training, improves content, tracks progress, and proves compliance — all in one platform. <button onClick={() => navigate('/what-is-cpdforge')} className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">What is CPDForge AI?</button>
            </p>
          </div>

          {/* Primary 3-Column Flow */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-14">
            {[
              { id: 'create', title: 'Create', caption: 'Generate structured, compliant training instantly', img: '/howit-create.webp', w: 1200, h: 800 },
              { id: 'improve', title: 'Improve', caption: 'AI reviews, identifies gaps, and enhances your courses', img: '/howit-improve.webp', w: 1200, h: 800, featured: true },
              { id: 'prove', title: 'Prove', caption: 'Export audit-ready evidence in one click', img: '/howit-prove.webp', w: 1200, h: 795 },
            ].map((card, i) => (
              <div key={card.id} className="relative group" data-testid={`story-card-${card.id}`}>
                {/* Subtle connector between cards (desktop only) */}
                {i < 2 && (
                  <div className="hidden md:block absolute -right-4 lg:-right-5 top-1/2 -translate-y-1/2 z-10 w-2 h-2 rounded-full bg-indigo-400/20 dark:bg-indigo-500/20 group-hover:bg-indigo-400/50 dark:group-hover:bg-indigo-500/50 transition-colors duration-500" />
                )}
                <div className={`relative rounded-2xl overflow-hidden border transition-all duration-300 ease-out
                  bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm
                  border-slate-200/80 dark:border-slate-800/80
                  group-hover:border-indigo-300/60 dark:group-hover:border-indigo-700/60
                  group-hover:shadow-lg group-hover:shadow-indigo-500/5 dark:group-hover:shadow-indigo-500/10
                  group-hover:-translate-y-1 group-hover:scale-[1.03]
                  ${card.featured ? 'ring-1 ring-indigo-200/40 dark:ring-indigo-800/40 group-hover:ring-indigo-400/50 dark:group-hover:ring-indigo-600/50 group-hover:shadow-xl group-hover:shadow-indigo-500/10 dark:group-hover:shadow-indigo-500/15' : ''}
                `}>
                  {/* Image */}
                  <div className="relative overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={card.img}
                      alt={`CPDForge AI ${card.title}`}
                      width={card.w}
                      height={card.h}
                      loading="lazy"
                      fetchpriority="low"
                      className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  </div>
                  {/* Text */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white
                        ${card.featured ? 'bg-gradient-to-br from-violet-500 to-indigo-600' : 'bg-gradient-to-br from-indigo-500 to-indigo-600'}
                      `}>{i + 1}</span>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">{card.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">{card.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Thin separator for visual rhythm */}
          <div className="w-16 h-px mx-auto bg-gradient-to-r from-transparent via-indigo-300/40 dark:via-indigo-700/40 to-transparent mb-14" />

          {/* Secondary Full-Width Sections */}
          <div className="space-y-10">
            {/* Identify Risk — Image LEFT, Text RIGHT */}
            <div className="grid md:grid-cols-2 gap-8 items-center group" data-testid="story-identify-risk">
              <div className="rounded-2xl overflow-hidden border border-slate-200/80 dark:border-slate-800/80 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm transition-all duration-300 group-hover:border-indigo-300/50 dark:group-hover:border-indigo-700/50 group-hover:shadow-lg group-hover:shadow-indigo-500/5">
                <img src="/howit-gaps.webp" alt="Training Gaps Detection" width={1200} height={779} loading="lazy" fetchpriority="low" className="w-full h-auto" />
              </div>
              <div className="flex flex-col justify-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800/40 bg-amber-50/60 dark:bg-amber-900/20 w-fit mb-4">
                  <AlertTriangle className="w-3.5 h-3.5" /> Risk Detection
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-4">Identify compliance risks before they become problems</h3>
                <ul className="space-y-3">
                  {['Spot overdue learners across every department', 'Surface missing mandatory training instantly', 'Department-level visibility for targeted action', 'Real-time risk awareness — no spreadsheets needed'].map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 mt-0.5 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Track Compliance — Text LEFT, Image RIGHT */}
            <div className="grid md:grid-cols-2 gap-8 items-center group" data-testid="story-track-compliance">
              <div className="flex flex-col justify-center order-2 md:order-1">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/40 bg-emerald-50/60 dark:bg-emerald-900/20 w-fit mb-4">
                  <TrendingUp className="w-3.5 h-3.5" /> Compliance Tracking
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-4">Track compliance performance across your organisation</h3>
                <ul className="space-y-3">
                  {['Measure progress over weeks and months', 'Prove measurable improvement to auditors', 'Give leadership clear compliance insight', 'Data-driven decisions replace guesswork'].map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-400">
                      <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl overflow-hidden border border-slate-200/80 dark:border-slate-800/80 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm transition-all duration-300 group-hover:border-emerald-300/50 dark:group-hover:border-emerald-700/50 group-hover:shadow-lg group-hover:shadow-emerald-500/5 order-1 md:order-2">
                <img src="/howit-trends.webp" alt="Compliance Trends" width={1200} height={801} loading="lazy" fetchpriority="low" className="w-full h-auto" />
              </div>
            </div>
          </div>

          {/* Functional explore CTA — smooth scrolls to next section */}
          <button
            onClick={() => document.querySelector('[data-testid="problem-section"]')?.scrollIntoView({ behaviour: 'smooth', block: 'start' })}
            className="mx-auto mt-6 mb-0 flex items-center gap-1.5 text-sm text-slate-400 dark:text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors cursor-pointer bg-transparent border-0"
            data-testid="explore-below-cta"
          >
            Explore each capability in detail below
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </button>
        </div>
      </section>

      {/* ═══════════ PROBLEM FRAMING ═══════════ */}
      <section className="py-16 bg-white dark:bg-slate-950" data-testid="problem-section">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader label="The Problem" title="Why compliance training software often fails to stay current" testId="problem-heading" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROBLEMS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-5" data-testid={`problem-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-red-500/8 dark:bg-red-500/10">
                  <Icon className="w-5 h-5 text-red-500 dark:text-red-400" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1.5">{title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ SOLUTION BRIDGE ═══════════ */}
      <section className="py-16 bg-gradient-to-b from-white to-indigo-50/40 dark:from-slate-950 dark:to-slate-900/40" data-testid="bridge-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold uppercase tracking-wider mb-3 bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">The Solution</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-5" data-testid="bridge-heading">
            An AI compliance training platform that closes the loop
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto">
            CPDForge AI combines AI course creation, non-destructive content improvement, regulatory intelligence, and full training management into a single platform — built for compliance teams, consultants, and training providers who need to move fast and stay ahead of regulation. <button onClick={() => navigate('/about')} className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">Learn about CPDForge</button>.
          </p>
        </div>
      </section>

      {/* ═══════════ CORE CAPABILITIES (Create, Improve, Enrich, Manage) ═══════════ */}
      <section className="py-20 relative overflow-hidden" data-testid="capabilities-section">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/60 via-violet-50/20 to-white dark:from-slate-900/60 dark:via-indigo-950/10 dark:to-slate-950" />
        <div className="relative max-w-6xl mx-auto px-6">
          <SectionHeader label="Platform Capabilities" title="How CPDForge AI works" testId="capabilities-heading" />
          <div className="grid md:grid-cols-2 gap-6">
            {CAPABILITIES.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.id} id={`cap-${cap.id}`} className="scroll-mt-24 bg-white/90 dark:bg-slate-900/70 backdrop-blur-sm border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-7 hover:border-opacity-60 transition-all hover:shadow-lg"
                  style={{ '--glow-rgb': cap.color }}
                  data-testid={`capability-${cap.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cap.color}12` }}>
                      <Icon className="w-5 h-5" style={{ color: cap.color }} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{cap.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-5">{cap.desc}</p>
                  <div className="space-y-2.5">
                    {cap.points.map(pt => (
                      <div key={pt} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: cap.color }} />
                        <span>{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ DETECT & MONITOR — PRIMARY PILLAR ═══════════ */}
      <section id="cap-detect" className="scroll-mt-20 py-20 relative overflow-hidden" data-testid="intelligence-section">
        <div className="absolute inset-0 bg-slate-900 dark:bg-slate-900/80" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[900px] h-[500px] rounded-full opacity-[0.08] blur-3xl bg-emerald-500" />
        <div className="hidden md:block absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full opacity-[0.05] blur-3xl bg-teal-500" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 mb-5" data-testid="intelligence-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Platform Differentiator
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-testid="intelligence-heading">
              Compliance Intelligence
            </h2>
            <p className="text-base text-slate-400 leading-relaxed max-w-2xl mx-auto">
              An AI-powered intelligence layer that continuously monitors your regulatory landscape, detects which training is impacted, and generates update plans — so your training never falls behind.
            </p>
            <p className="text-sm font-semibold text-emerald-400 mt-4">
              This is what separates CPDForge AI from every other AI course tool.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INTELLIGENCE.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-5 hover:border-emerald-600/40 transition-colors" data-testid={`intel-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-emerald-500/10">
                  <Icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1.5">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
            <span>AI creates update proposals — you review and approve before any content changes are applied.</span>
          </div>

          {/* Compliance Framework Coverage Table */}
          <div className="mt-12 max-w-4xl mx-auto">
            <h3 className="text-lg font-bold text-white text-center mb-6">Regulatory Framework Coverage</h3>
            <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-3 gap-4 bg-emerald-600/20 border-b border-emerald-500/30 px-6 py-3">
                <div className="text-xs font-bold text-emerald-300">Framework</div>
                <div className="text-xs font-bold text-emerald-300 text-center">Coverage</div>
                <div className="text-xs font-bold text-emerald-300 text-center">Auto-Update</div>
              </div>
              
              {/* Table Rows */}
              <div className="divide-y divide-slate-700/40">
                {[
                  { name: 'ISO 9001 / 45001 / 14001', coverage: 'Full' },
                  { name: 'HSE & OSHA Standards', coverage: 'Full' },
                  { name: 'GDPR & Data Protection', coverage: 'Full' },
                  { name: 'ACAS Employment Law', coverage: 'Full' },
                  { name: 'ESG & Sustainability', coverage: 'Full' },
                ].map((framework) => (
                  <div key={framework.name} className="grid grid-cols-3 gap-4 px-6 py-3 hover:bg-slate-700/20 transition-colors">
                    <div className="text-sm text-slate-300">{framework.name}</div>
                    <div className="flex items-center justify-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" />
                        {framework.coverage}
                      </span>
                    </div>
                    <div className="flex items-center justify-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300">
                        <Zap className="w-3 h-3" />
                        Yes
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ AI REVIEW SPOTLIGHT ═══════════ */}
      <section className="py-20 relative overflow-hidden" data-testid="ai-review-spotlight">
        <div className="absolute inset-0 bg-gradient-to-b from-violet-50/40 via-white to-white dark:from-violet-950/15 dark:via-slate-950 dark:to-slate-950" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] h-[400px] rounded-full opacity-[0.06] dark:opacity-[0.04] blur-3xl bg-violet-500" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="text-center mb-3">
            <p className="text-sm font-semibold text-violet-600 dark:text-violet-400 mb-3">Key Differentiator</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-4" data-testid="ai-review-heading">
              AI-Powered Course Improvement
            </h2>
            <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto mb-3">
              After your course is created, AI Review analyses it for training gaps, weak content, and missing compliance topics — then proposes improvements you approve before they're applied.
            </p>
            <p className="text-sm font-semibold text-violet-600 dark:text-violet-400" data-testid="ai-review-outcome-line">
              Most tools stop at generation. CPDForge improves your training automatically.
            </p>
          </div>
          <div className="mt-10 rounded-2xl border border-violet-300/60 dark:border-violet-700/40 overflow-hidden bg-white dark:bg-slate-900/80 shadow-lg shadow-violet-500/[0.08]">
            <div className="h-1.5 w-full bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" />
            <div className="md:grid md:grid-cols-[1fr_auto]">
              <div className="p-8 md:p-10">
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {['Non-destructive — enhances, never replaces', 'Adds new lessons and topics to fill gaps', 'Strengthens depth and compliance alignment', 'Section-level rewrite, expand, and scenarios'].map(bullet => (
                    <div key={bullet} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
                      <Check className="w-4 h-4 mt-0.5 text-violet-500 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6" data-testid="ai-review-before-after">
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-800/30 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">Before</p>
                    <ul className="space-y-1.5">
                      <li className="flex items-center gap-2 text-sm text-slate-500"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />Missing topics</li>
                      <li className="flex items-center gap-2 text-sm text-slate-500"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />Limited depth</li>
                      <li className="flex items-center gap-2 text-sm text-slate-500"><span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />Compliance gaps</li>
                    </ul>
                  </div>
                  <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-800/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-2">After AI Review</p>
                    <ul className="space-y-1.5">
                      <li className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />New lessons added</li>
                      <li className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />Stronger instructional quality</li>
                      <li className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300"><Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />Compliance gaps closed</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-lg px-3 py-2 max-w-fit">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span>Original content is always preserved — AI Review enhances without overwriting.</span>
                </div>
                <div className="mt-4">
                  <button onClick={() => navigate('/features#ai-powered-course-improvement')} className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold hover:underline inline-flex items-center gap-1">
                    See all AI capabilities <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center p-6 md:pr-8 md:max-w-[380px]">
                <LandingDemoVideo
                  src="https://assets.healthandsafetycollege.com/videos/ai-review-demo.mp4"
                  caption="AI Review analyses your course and proposes improvements you approve."
                  onExpand={() => setLightboxVideo('https://assets.healthandsafetycollege.com/videos/ai-review-demo.mp4')}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ EXPORT & DEPLOY ═══════════ */}
      <section className="py-20 bg-white dark:bg-slate-950" data-testid="export-section">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader label="Export & Deployment" title="Deploy training anywhere" testId="export-heading" />
          <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto text-center -mt-8 mb-10">
            Export courses in industry-standard formats and deploy them across any learning management system, presentation, or training environment.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {EXPORTS.map(({ label, desc, color }) => (
              <div key={label} className="bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 rounded-xl p-5 text-center hover:border-opacity-60 transition-all" data-testid={`export-${label.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${color}10` }}>
                  <Download className="w-5 h-5" style={{ color }} />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">{label}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500 dark:text-slate-400">
            {['Export to SCORM for any LMS', 'Download as PowerPoint or PDF instantly', 'Instructor guidance toolkit included'].map(item => (
              <span key={item} className="flex items-center gap-2"><Check className="w-4 h-4 text-indigo-500 flex-shrink-0" />{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BUILT FOR SCALE ═══════════ */}
      <section className="py-20 bg-gradient-to-b from-indigo-50/40 to-white dark:from-slate-900/40 dark:to-slate-950" data-testid="scale-section">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader label="Built for Scale" title="From solo trainers to multi-client consultancies" testId="scale-heading" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Building2, title: 'Multi-Organisation Management', desc: 'Manage training for multiple client organisations from a single partner dashboard. Isolated data, settings, and branding per client.' },
              { icon: Palette, title: 'White-Label Delivery', desc: 'Brand the platform with client logos, colours, and custom certificate templates. Your clients see your brand, not ours.' },
              { icon: Users, title: 'Team & Department Structure', desc: 'Organise learners by team or department. Assign courses at group level and auto-cascade to new joiners.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-6 hover:border-indigo-300/70 dark:hover:border-indigo-700/50 hover:shadow-lg transition-all" data-testid={`scale-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/50 dark:to-violet-950/50">
                  <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1.5">{title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 text-xs text-slate-600 dark:text-slate-400">
              <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
              <span><strong className="text-slate-800 dark:text-slate-200">Bring Your Own AI Key</strong> — connect your own OpenAI or Gemini API key for expanded or unlimited generation</span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ WHO IT'S FOR ═══════════ */}
      <section className="py-16 bg-white dark:bg-slate-950" data-testid="audience-section">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader label="Who It's For" title="Built for Training Professionals" testId="audience-heading" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {AUDIENCES.map(({ icon: Icon, title, desc }) => (
              <div key={title} data-testid={`audience-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="card-glow group bg-white dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-6 hover:border-indigo-300/70 dark:hover:border-indigo-700/50"
                style={{ '--glow-rgb': '99,102,241' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/50 dark:to-violet-950/50 group-hover:shadow-md group-hover:shadow-indigo-500/10 transition-shadow">
                  <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1.5">{title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightweight assessment link — re-engages scrollers */}
      <div className="py-6 text-center">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Comparing platforms?{' '}
          <button
            onClick={() => {
              trackAssessmentCTA({
                text: 'Find your best match in 3 minutes',
                type: 'text_link',
                destination: '/compliance-training-assessment',
                position: 'mid_scroll',
                pageType: 'homepage',
              });
              sessionStorage.setItem('assessment_entry_source', '/');
              sessionStorage.setItem('assessment_cta_location', 'mid_scroll');
              navigate('/compliance-training-assessment');
            }}
            className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline inline-flex items-center gap-1"
            data-testid="homepage-mid-scroll-assessment-link"
          >
            Find your best match in 3 minutes <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </p>
      </div>

      {/* ═══════════ TESTIMONIALS ═══════════ */}
      <section className="py-20 bg-white dark:bg-slate-950" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader label="Testimonials" title="What Early Users Are Saying" testId="testimonials-heading" />

          {/* Trust counter */}
          <div className="flex items-center justify-center gap-6 mb-12 -mt-6" data-testid="trust-counter">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[
                  'https://assets.healthandsafetycollege.com/site-assets/testimonials/avatar-1.webp',
                  'https://assets.healthandsafetycollege.com/site-assets/testimonials/avatar-2.webp',
                  'https://assets.healthandsafetycollege.com/site-assets/testimonials/avatar-3.webp',
                  'https://assets.healthandsafetycollege.com/site-assets/testimonials/avatar-4.webp',
                ].map((src, i) => (
                  <img key={i} src={src} alt="" width={32} height={32} loading="lazy" className="w-8 h-8 rounded-full border-2 border-white dark:border-slate-950 object-cover" />
                ))}
              </div>
              <span className="text-sm text-slate-600 dark:text-slate-400"><strong className="text-slate-900 dark:text-white">120+</strong> organisations building training</span>
            </div>
            <span className="hidden sm:inline text-slate-300 dark:text-slate-700">|</span>
            <div className="hidden sm:flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              <span className="text-sm text-slate-600 dark:text-slate-400 ml-1"><strong className="text-slate-900 dark:text-white">4.9</strong> average rating</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { quote: 'We went from spending days building training materials to generating complete courses in under 30 minutes. The AI Review feature alone saved us weeks of manual content improvement.', name: 'Liam M.', role: 'Compliance Manager', industry: 'Manufacturing' },
              { quote: "The quality enforcement is what sold us. Every lesson meets a minimum standard — no thin content, no placeholders. It's the first AI tool our compliance team actually trusts.", name: 'Abbie S.', role: 'Head of Training', industry: 'Healthcare' },
              { quote: 'The regulatory monitoring changes everything. We used to find out about regulation changes weeks late. Now the platform flags impacted courses before we even know about the change.', name: 'Tia R.', role: 'H&S Consultant', industry: 'Construction' },
              { quote: 'Our clients used to wait weeks for new training materials. Now I generate a full course, customise it, and share it within the hour. It completely changed how we deliver value.', name: 'James A.', role: 'Training Consultant', industry: 'Facilities Management' },
              { quote: 'The learner tracking and certificate generation closed the loop for us. We can now prove compliance to auditors with actual data instead of spreadsheets and screenshots.', name: 'Jack D.', role: 'Operations Director', industry: 'Logistics' },
              { quote: 'I was sceptical about AI-generated training — but the content quality genuinely surprised me. The courses are structured, specific, and audit-ready out of the box.', name: 'Jan C.', role: 'Quality Manager', industry: 'Food Manufacturing' },
            ].map(({ quote, name, role, industry }) => (
              <div key={name} className="bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-6" data-testid={`testimonial-${name.toLowerCase().replace(/[\s.]+/g, '-')}`}>
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-5">"{quote}"</p>
                <div className="border-t border-slate-200/60 dark:border-slate-800/60 pt-4">
                  <p className="text-base font-bold text-slate-900 dark:text-white">{name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{role} &bull; {industry}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ PRICING PREVIEW ═══════════ */}
      <section className="py-20 bg-white dark:bg-slate-950" data-testid="pricing-preview-section">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeader label="Pricing" title="Simple, Transparent Pricing" testId="pricing-preview-heading" />
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center -mt-8 mb-12 max-w-lg mx-auto">Self-serve plans include a {trialDays}-day free trial. No credit card required.</p>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {PLANS_PREVIEW.map(({ name, price, desc, color, rgb, popular, bestFor, keyMetric, gradient, gradientDark }) => (
              <div key={name} data-testid={`pricing-preview-${name.toLowerCase()}`}
                className={`relative rounded-2xl border transition-all duration-300 group ${popular ? 'scale-[1.03] border-transparent' : 'border-slate-200/80 dark:border-slate-700/50 hover:border-transparent'}`}
                style={{ background: isDark ? gradientDark : gradient, '--glow-rgb': rgb, ...(popular ? { boxShadow: `0 8px 40px rgba(${rgb},0.18), 0 0 0 2px rgba(${rgb},0.25)` } : {}) }}
                onMouseEnter={(e) => { if (!popular) e.currentTarget.style.boxShadow = `0 10px 40px rgba(${rgb},0.14), 0 0 0 1px rgba(${rgb},0.2)`; }}
                onMouseLeave={(e) => { if (!popular) e.currentTarget.style.boxShadow = ''; }}>
                <div className="h-1 w-full rounded-t-2xl" style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
                {popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white shadow-lg z-10" style={{ backgroundColor: color, boxShadow: `0 4px 20px rgba(${rgb},0.4)` }}>
                    Most Popular
                  </span>
                )}
                <div className="p-7 text-center">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold mb-3" style={{ backgroundColor: `${color}12`, color }} data-testid={`preview-best-for-${name.toLowerCase()}`}>
                    Best for {bestFor}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">{name}</h3>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border mb-3" style={{ borderColor: `${color}25`, backgroundColor: `${color}06` }} data-testid={`preview-metric-${name.toLowerCase()}`}>
                    <Sparkles className="w-3 h-3" style={{ color }} />
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{keyMetric}</span>
                  </div>
                  <div className="flex items-baseline justify-center gap-0.5 mb-2">
                    <span className="text-3xl font-extrabold text-slate-900 dark:text-white">&pound;{price}</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">/ mo</span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">{desc}</p>
                  <p className={`text-xs font-semibold mb-4 ${popular ? 'text-violet-600 dark:text-violet-400' : 'text-slate-400 dark:text-slate-500'}`} data-testid={`pricing-tier-${name.toLowerCase()}`}>
                    {popular ? 'Unlock AI improvement, exports, and compliance intelligence' : 'AI course generation and learner management'}
                  </p>
                  <Button onClick={() => handleTrialCTA(name.toLowerCase())} data-testid={`pricing-preview-cta-${name.toLowerCase()}`} data-cta="primary-trial"
                    className={`btn-shine w-full h-10 rounded-xl text-sm font-semibold transition-all ${popular ? 'text-white shadow-md border-0' : 'bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                    style={popular ? { backgroundColor: color } : { borderColor: color, color }}>
                    Start Free Trial
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => navigate('/pricing')} data-testid="see-all-plans-link"
              className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors inline-flex items-center gap-1.5">
              See all plans including Partner and Enterprise <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════ CTA + AUTH ═══════════ */}
      <section id="auth" className="py-20 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/50 to-white dark:from-slate-900/40 dark:to-slate-950" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] h-[400px] rounded-full opacity-[0.06] dark:opacity-[0.04] blur-3xl bg-violet-500" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight mb-4" data-testid="cta-heading">
                Start building better compliance training today
              </h2>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed mb-8">
                One platform to create, improve, manage, and stay ahead of regulation. Start your free trial and see the difference in minutes.
              </p>
              <div className="space-y-3.5">
                {[
                  'AI generates AND improves your courses automatically',
                  'Regulatory monitoring detects impacted training for you',
                  'Full learner management with certificates and tracking',
                  'Export to SCORM, PowerPoint, PDF, and HTML',
                  `${trialDays}-day free trial with full platform access`,
                ].map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-sm shadow-indigo-500/20">
                      <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-slate-400 dark:text-slate-500" data-testid="cta-microcopy">
                No credit card required &bull; Start in under 60 seconds
              </p>
            </div>

            <div ref={authRef}>
              {/* Trial Context Banner */}
              {intentParam === 'trial' && authMode === 'register' && (
                <div className="mb-4 flex items-center gap-3 px-5 py-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-xl max-w-md mx-auto" data-testid="trial-context-banner">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    Starting your {trialDays}-day free trial — Full platform access, no credit card required
                  </span>
                </div>
              )}
              
              <div className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl shadow-xl shadow-indigo-500/[0.06] dark:shadow-slate-950/60 p-8 max-w-md mx-auto" data-testid="auth-form-card">
                <div className="flex items-center gap-3 mb-6">
                  <img src={logoSrc} alt={brandName} className="w-8 h-8 rounded-lg" />
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {authMode === 'login' ? 'Welcome Back' : authMode === 'register' ? (intentParam === 'programme' ? 'Create your account' : intentParam === 'buy' ? 'Get Started' : 'Start Your Free Trial') : 'Reset Password'}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {authMode === 'login' ? `Sign in to ${brandName}` : authMode === 'register' ? (intentParam === 'programme' ? 'Create your account to complete this purchase' : intentParam === 'buy' ? 'Create your account and subscribe' : `Create your ${brandName} account`) : 'Enter your email to receive a reset link'}
                    </p>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* P1 / Item 3: MFA challenge form */}
                  {authMode === 'mfa' && (
                    <div className="space-y-3">
                      <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900 rounded-lg p-3 text-sm text-indigo-900 dark:text-indigo-200">
                        Enter the 6-digit code from your authenticator app to finish signing in.
                      </div>
                      <div>
                        <Label htmlFor="mfa-code" className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                          {isRecoveryCode ? 'Recovery code' : 'Authentication code'}
                        </Label>
                        <Input
                          id="mfa-code"
                          type="text"
                          inputMode={isRecoveryCode ? 'text' : 'numeric'}
                          autoComplete="one-time-code"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(isRecoveryCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ''))}
                          placeholder={isRecoveryCode ? 'XXXX-XXXX-XX' : '123456'}
                          required
                          data-testid="auth-mfa-code-input"
                          className="h-11 bg-slate-50/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white text-center tracking-widest font-mono"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => { setIsRecoveryCode(!isRecoveryCode); setMfaCode(''); }}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                        data-testid="auth-mfa-toggle-recovery"
                      >
                        {isRecoveryCode ? 'Use authenticator code instead' : 'Use a recovery code instead'}
                      </button>
                      {!isRecoveryCode && (
                        <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1" data-testid="auth-mfa-trust-device-label">
                          <input
                            type="checkbox"
                            checked={trustDevice}
                            onChange={(e) => setTrustDevice(e.target.checked)}
                            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            data-testid="auth-mfa-trust-device-checkbox"
                          />
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            Trust this device for 30 days (skip MFA on this browser)
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                  {authMode !== 'mfa' && rateLimited && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3.5" data-testid="rate-limit-banner">
                      <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">Too many attempts. Please wait a few minutes.</p>
                      <button type="button" onClick={() => { setRateLimited(false); setAuthMode('reset'); }} data-testid="rate-limit-forgot-link"
                        className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 mt-1.5 transition-colors">
                        Forgot your password? Reset it here &rarr;
                      </button>
                    </div>
                  )}
                  <div className={authMode === 'mfa' ? 'hidden' : ''}>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Email Address</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required={authMode !== 'mfa'} data-testid="auth-email-input"
                      className="h-11 bg-slate-50/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 focus:border-indigo-500 dark:focus:border-indigo-500 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500" />
                  </div>
                  {authMode !== 'reset' && authMode !== 'mfa' && (
                    <div>
                      <Label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Password</Label>
                      <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="........" required data-testid="auth-password-input"
                        className="h-11 bg-slate-50/80 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 focus:border-indigo-500 dark:focus:border-indigo-500 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500" />
                      {authMode === 'register' && (
                        <p className="mt-1.5 text-xs text-slate-400 dark:text-slate-500" data-testid="password-hint">
                          {password.length === 0 ? 'Minimum 8 characters' : password.length < 8 ? `${8 - password.length} more character${8 - password.length === 1 ? '' : 's'} needed` : ''}
                          {password.length >= 8 && <span className="text-emerald-500 dark:text-emerald-400">Strong enough</span>}
                        </p>
                      )}
                    </div>
                  )}
                  {authMode === 'login' && (
                    <div className="text-right">
                      <button type="button" onClick={() => setAuthMode('reset')} data-testid="forgot-password-link"
                        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors">
                        Forgot password?
                      </button>
                    </div>
                  )}
                  {authMode === 'register' && intentParam !== 'programme' && (
                    <div data-testid="workspace-type-selector">
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Choose your plan</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {SIGNUP_TIERS.map((tier) => (
                          <button key={tier.id} type="button" data-testid={`tier-${tier.id}`}
                            onClick={() => setSelectedTier(tier.id)}
                            className={`relative p-3 rounded-xl border-2 text-left transition-all duration-200 ${
                              selectedTier === tier.id
                                ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/40 shadow-md shadow-indigo-500/15 ring-1 ring-indigo-500/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm'
                            }`}>
                            {tier.popular && (
                              <span className="absolute -top-2 right-2 px-2 py-0.5 text-[10px] font-bold uppercase bg-indigo-600 text-white rounded-full leading-tight">Popular</span>
                            )}
                            <div className={`font-bold text-sm ${selectedTier === tier.id ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>{tier.label}</div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">{tier.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <Button type="submit" disabled={loading} data-testid="auth-submit-button"
                    className="btn-shine w-full h-11 text-white rounded-full font-semibold transition-all active:scale-[0.97] shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-0">
                    {loading ? 'Please wait...' : authMode === 'mfa' ? 'Verify code' : authMode === 'login' ? 'Sign In' : authMode === 'register' ? (intentParam === 'programme' ? 'Continue to checkout' : intentParam === 'buy' ? 'Create Account & Subscribe' : 'Start Free Trial') : 'Send Reset Link'}
                  </Button>
                </form>
                <div className="mt-4 text-center space-y-2">
                  {authMode === 'mfa' ? (
                    <>
                      <button type="button" onClick={() => { setAuthMode('login'); setMfaPendingToken(null); setMfaCode(''); setIsRecoveryCode(false); }} data-testid="auth-mfa-back"
                        className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors">
                        Cancel and sign in again
                      </button>
                      <a href="/help/mfa-guide" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-500 transition-colors" data-testid="auth-mfa-help-link">
                        Need help with MFA?
                      </a>
                    </>
                  ) : authMode === 'reset' ? (
                    <button type="button" onClick={() => setAuthMode('login')} data-testid="auth-back-to-login"
                      className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors">
                      Back to Sign In
                    </button>
                  ) : (
                    <button type="button" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')} data-testid="auth-toggle-button"
                      className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 transition-colors">
                      {authMode === 'login' ? "Don't have an account? Start free trial" : 'Already have an account? Sign in'}
                    </button>
                  )}
                  {authMode === 'login' && (
                    <div className="pt-1 border-t border-slate-100 dark:border-slate-800/60 mt-1">
                      <button type="button" data-testid="resend-verification-link"
                        disabled={resendingVerification || !email}
                        onClick={async () => {
                          if (!email) { toast.error('Enter your email address first'); return; }
                          setResendingVerification(true);
                          try {
                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification-public`, { email });
                            toast.success('If an unverified account exists, a verification email has been sent.');
                          } catch { toast.error('Something went wrong. Try again later.'); }
                          finally { setResendingVerification(false); }
                        }}
                        className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                        {resendingVerification ? 'Sending...' : "Didn't get a verification email? Resend it"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ STANDARD LIBRARY SECTION ═══════════ */}
      <section className="py-16 bg-gradient-to-b from-white to-indigo-50/30 dark:from-slate-950 dark:to-indigo-950/10 border-t border-slate-200/60 dark:border-slate-800/60" data-testid="library-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider mb-3 bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Standard Compliance Library</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-testid="library-section-heading">
              {libStats?.courses || '50'}+ Ready-to-Deploy Compliance Courses
            </h2>
            <p className="mt-3 text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Professionally authored training covering {libStats?.pillars || 5} compliance pillars and {libStats?.regulations_covered || '80'}+ regulations. Deploy to your workforce in minutes — or use alongside AI-generated courses.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 mb-8">
            {[
              { label: 'Courses', value: libStats?.courses || '50', sub: 'Ready to deploy' },
              { label: 'Lessons', value: libStats?.lessons || '250', sub: `${libStats?.learning_hours || '80'}+ learning hours` },
              { label: 'Pillars', value: libStats?.pillars || 5, sub: 'Full compliance coverage' },
            ].map(s => (
              <div key={s.label} className="text-center p-5 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80" data-testid={`library-stat-${s.label.toLowerCase()}`}>
                <p className="text-3xl font-extrabold text-indigo-600 dark:text-indigo-400">{s.value}{s.label === 'Courses' && '+'}</p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white mt-1">{s.label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{s.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { text: 'Instant deployment — no content creation needed', icon: Zap },
              { text: 'Regulation-aligned with CPD certification', icon: Award },
              { text: 'Works alongside AI-generated courses', icon: Sparkles },
              { text: 'Full access during your 14-day free trial', icon: ShieldCheck },
            ].map(b => {
              const Icon = b.icon;
              return (
                <div key={b.text} className="flex items-start gap-2.5 p-3 rounded-lg bg-white/60 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-800/60">
                  <Icon className="w-4 h-4 text-indigo-500 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{b.text}</span>
                </div>
              );
            })}
          </div>

          <div className="text-center space-y-3">
            <button onClick={() => navigate('/library')} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors shadow-lg shadow-indigo-500/20" data-testid="library-section-browse-cta">
              Browse the Library <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400">From £9.80/month with select plans</p>
          </div>
        </div>
      </section>

      {/* ═══════════ SPECIALIST PROGRAMMES RAIL (P0-2 — data-driven) ═══════════ */}
      <ProgrammesRail
        source="homepage"
        className="py-16 bg-white dark:bg-slate-950 border-t border-slate-200/60 dark:border-slate-800/60"
        eyebrow="Specialist Credentials"
        title="Earn a verifiable credential"
        subtitle="Credentialed, multi-course pathways for practitioners and auditors — each issues a verifiable credential (Level 3–5 equivalent), quality-assured and human-reviewed."
      />

      {/* ═══════════ COMPARE PROVIDERS BANNER ═══════════ */}
      <section className="py-16 border-t border-slate-200/60 dark:border-slate-800/60" data-testid="compare-providers-banner">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-10">
            {/* 1. Updated label: Honest → Independent */}
            <p className="text-sm font-semibold uppercase tracking-wider mb-3 bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Independent Comparisons</p>
            
            {/* 2. Headline (unchanged - already optimal) */}
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-testid="compare-banner-heading">Compare Compliance Training Providers</h2>
            
            {/* 3. Updated subhead */}
            <p className="mt-3 text-base text-slate-500 dark:text-slate-400 max-w-3xl">See how CPDForge compares to traditional compliance training providers — from pre-built course libraries to enterprise platforms — across automation, regulatory intelligence, and audit-ready compliance.</p>
            
            {/* 4. NEW: Differentiation line */}
            <p className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-300 max-w-3xl">
              Most providers deliver static courses. CPDForge combines AI course generation, continuous improvement, and regulatory intelligence to keep your training aligned, up to date, and audit-ready — automatically.
            </p>
          </div>
          
          {/* 6. Updated card descriptions */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { path: '/ihasco-alternative', name: 'iHASCO', tag: 'Pre-built course library — limited flexibility when regulations change' },
              { path: '/high-speed-training-alternative', name: 'High Speed Training', tag: 'Pay-per-course model — manual updates required' },
              { path: '/skillcast-alternative', name: 'Skillcast', tag: 'Managed compliance training — limited automation' },
              { path: '/vinciworks-alternative', name: 'VinciWorks', tag: 'Legal compliance focus — less AI-driven improvement' },
              { path: '/navex-alternative', name: 'NAVEX', tag: 'Enterprise GRC platform — complex and resource-heavy' },
              { path: '/traliant-alternative', name: 'Traliant', tag: 'Video-based training — static content delivery' },
            ].map((c, i) => (
              <button
                key={i}
                onClick={() => navigate(c.path)}
                className="group p-4 rounded-xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900/50 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all text-left"
                data-testid={`compare-card-${c.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <p className="font-bold text-sm text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors leading-tight">{c.name}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 leading-tight">{c.tag}</p>
                <p className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Compare <ArrowRight className="w-3 h-3" /></p>
              </button>
            ))}
          </div>
          
          {/* 5. NEW: Conversion bullets (below cards) */}
          <div className="mt-8 max-w-2xl mx-auto text-center">
            <p className="text-sm font-bold text-slate-900 dark:text-white mb-3">Why teams switch to CPDForge:</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Training updates automatically when regulations change</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Identify compliance gaps before audits</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span>Generate audit & accreditation-ready evidence</span>
              </div>
            </div>
          </div>
          
          {/* 7. Updated CTAs */}
          <div className="mt-8 text-center space-y-3">
            {/* Primary CTA */}
            <button onClick={() => navigate('/best-compliance-training-platforms')} className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1.5 mx-auto" data-testid="compare-banner-hub-link">
              See how CPDForge compares in detail <ArrowRight className="w-4 h-4" />
            </button>
            
            {/* Secondary CTA */}
            <div>
              <Button 
                onClick={() => navigate('/?intent=trial#auth')} 
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="compare-trial-cta"
              >
                Start your free trial <ArrowRight className="w-4 h-4 inline ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FOOTER ═══════════ */}
      {/* Cross-page navigation prompt */}
      <div className="py-8 text-center bg-slate-50/60 dark:bg-slate-900/40 border-t border-slate-200/60 dark:border-slate-800/60">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Want to see all capabilities in detail? <button onClick={() => navigate('/features')} className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline inline-flex items-center gap-1">Explore Features <ArrowRight className="w-3.5 h-3.5" /></button>
        </p>
      </div>
      
      <SEOFooter brandName={brandName} logoSrc={logoSrc} />

      {/* ═══════════ OVERLAYS ═══════════ */}
      {/* Exit Intent Modal */}
      {showExitIntent && !user && !exitEmailSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowExitIntent(false)} data-testid="exit-intent-modal">
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowExitIntent(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center mx-auto mb-4">
                <Download className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Before you go...</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Get a free compliance gap assessment checklist to identify training risks in your organisation.</p>
              <form onSubmit={(e) => { e.preventDefault(); setExitEmailSubmitted(true); toast.success('Checklist sent! Check your email.'); setTimeout(() => setShowExitIntent(false), 1500); }} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  required
                  className="w-full"
                  data-testid="exit-email-input"
                />
                <Button type="submit" className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" data-testid="exit-submit-btn">
                  Send Checklist
                </Button>
              </form>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">No spam. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      )}
      
      {lightboxVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8" onClick={() => setLightboxVideo(null)} data-testid="landing-video-lightbox">
          <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxVideo(null)} className="absolute -top-10 right-0 flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-sm font-medium" data-testid="landing-lightbox-close">
              Close <X className="w-5 h-5" />
            </button>
            <video autoPlay loop muted controls playsInline className="w-full rounded-xl shadow-2xl">
              <source src={lightboxVideo} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
      <TrialModal
        isOpen={trialModalOpen}
        onClose={handleTrialModalClose}
        onCreateAccount={handleTrialModalCreate}
        onSignIn={handleTrialModalSignIn}
      />
      <BackToTop />
    </div>
  );
}

function SectionHeader({ label, title, testId }) {
  return (
    <div className="text-center mb-14">
      <p className="text-sm font-semibold uppercase tracking-wider mb-3 bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">{label}</p>
      <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-testid={testId}>{title}</h2>
    </div>
  );
}
